import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from './api';
import { useAuth } from '../../context/AuthContext';
import { Modal } from './ClientsPage';

const STATUS_COLOR = { draft:'#9A8A7A', review:'#B8860B', approved:'#2A7A5A', archived:'#A09080' };
const STATUS_ICON  = { draft:'✏️', review:'🔍', approved:'✅', archived:'📦' };

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canEdit, canApprove, isAdmin } = useAuth();

  const [client, setClient]   = useState(null);
  const [plans, setPlans]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal]     = useState(false);
  const [form, setForm]       = useState({ name:'', year: new Date().getFullYear() });
  const [saving, setSaving]   = useState(false);
  const [filter, setFilter]   = useState('all');

  async function load() {
    const [c, p] = await Promise.all([api.getClient(id), api.getPlans(id)]);
    setClient(c); setPlans(p);
    setLoading(false);
  }
  useEffect(() => { load(); }, [id]);

  async function createPlan() {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const plan = await api.createPlan({ client_id: id, name: form.name, year: form.year, data: {} });
      navigate(`/plans/${plan.id}`);
    } catch(e) { alert(e.message); }
    finally { setSaving(false); }
  }

  async function handleAction(e, planId, action) {
    e.stopPropagation();
    if (!window.confirm(`${action} this plan?`)) return;
    try {
      if (action === 'submit')  await api.submitPlan(planId);
      if (action === 'approve') await api.approvePlan(planId);
      if (action === 'reject')  await api.rejectPlan(planId);
      if (action === 'archive') await api.archivePlan(planId);
      if (action === 'delete')  { await api.deletePlan(planId); }
      load();
    } catch(e) { alert(e.message); }
  }

  const filtered = filter === 'all' ? plans : plans.filter(p => p.status === filter);

  if (loading) return <div style={{ color:'#9A8A7A', padding:40 }}>Loading…</div>;
  if (!client) return <div style={{ color:'#A02020', padding:40 }}>Client not found.</div>;

  return (
    <div style={{padding:'28px 32px'}}>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:24 }}>
        <button style={styles.btnGhost} onClick={() => navigate('/clients')}>← Clients</button>
        <div>
          <h1 style={styles.h1}>{client.name}</h1>
          {client.industry && <div style={{ fontSize:13, color:'#9A8A7A', marginTop:2 }}>{client.industry}</div>}
        </div>
        <div style={{ flex:1 }}/>
        {canEdit && (
          <button style={styles.btnPrimary} onClick={() => setModal(true)}>+ New Plan</button>
        )}
      </div>

      {/* Status filter tabs */}
      <div style={{ display:'flex', gap:6, marginBottom:18, flexWrap:'wrap' }}>
        {['all','draft','review','approved','archived'].map(s => (
          <button key={s} onClick={() => setFilter(s)} style={{
            padding:'5px 14px', borderRadius:20, fontSize:12, fontWeight:600,
            cursor:'pointer', border:'1px solid',
            borderColor: filter===s ? '#C19A6B' : '#E0E0E0',
            background: filter===s ? '#C9A84C22' : 'transparent',
            color: filter===s ? '#C19A6B' : '#9A8A7A'
          }}>
            {s === 'all' ? `All (${plans.length})` : `${STATUS_ICON[s]} ${s} (${plans.filter(p=>p.status===s).length})`}
          </button>
        ))}
      </div>

      {/* Plans list */}
      {filtered.length === 0 ? (
        <div style={styles.empty}>
          {filter === 'all' ? 'No plans yet for this client.' : `No ${filter} plans.`}
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
          {filtered.map(plan => (
            <div key={plan.id} style={styles.planRow} onClick={() => navigate(`/plans/${plan.id}`)}>
              <div style={{
                width:4, borderRadius:4, flexShrink:0,
                background: STATUS_COLOR[plan.status]
              }}/>
              <div style={{ flex:1 }}>
                <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:4 }}>
                  <span style={{ fontWeight:700, fontSize:14, color:'#0A0A0A' }}>{plan.name}</span>
                  <span style={{
                    padding:'2px 8px', borderRadius:10, fontSize:11, fontWeight:600,
                    color: STATUS_COLOR[plan.status],
                    background: STATUS_COLOR[plan.status]+'22'
                  }}>{STATUS_ICON[plan.status]} {plan.status}</span>
                  {plan.year && <span style={{ fontSize:11, color:'#A09080' }}>{plan.year}</span>}
                </div>
                <div style={{ fontSize:11, color:'#A09080' }}>
                  v{plan.version} · Updated {new Date(plan.updated_at).toLocaleDateString()} by {plan.updated_by_name || plan.created_by_name}
                </div>
              </div>

              {/* Action buttons */}
              <div style={{ display:'flex', gap:6, flexShrink:0 }} onClick={e=>e.stopPropagation()}>
                {plan.status === 'draft' && canEdit && (
                  <button style={styles.btnAction} onClick={e => handleAction(e, plan.id, 'submit')}>
                    Submit for Review
                  </button>
                )}
                {plan.status === 'review' && canApprove && (<>
                  <button style={{ ...styles.btnAction, color:'#2A7A5A', borderColor:'#3FB95044' }}
                    onClick={e => handleAction(e, plan.id, 'approve')}>✓ Approve</button>
                  <button style={{ ...styles.btnAction, color:'#A02020', borderColor:'#F8514944' }}
                    onClick={e => handleAction(e, plan.id, 'reject')}>✗ Reject</button>
                </>)}
                {isAdmin && plan.status !== 'archived' && (
                  <button style={{ ...styles.btnAction, color:'#A09080' }}
                    onClick={e => handleAction(e, plan.id, 'archive')}>Archive</button>
                )}
                {isAdmin && (
                  <button style={{ ...styles.btnAction, color:'#A02020' }}
                    onClick={e => handleAction(e, plan.id, 'delete')}>Delete</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* New plan modal */}
      {modal && (
        <Modal title="New Plan" onClose={() => setModal(false)}>
          <label style={{ display:'block', marginBottom:14 }}>
            <div style={styles.label}>Plan Name *</div>
            <input value={form.name} onChange={e => setForm(p=>({...p,name:e.target.value}))}
              placeholder="e.g. Q1 2026 Campaign" style={styles.input} autoFocus />
          </label>
          <label style={{ display:'block', marginBottom:20 }}>
            <div style={styles.label}>Year</div>
            <input type="number" value={form.year} onChange={e => setForm(p=>({...p,year:+e.target.value}))}
              style={styles.input} />
          </label>
          <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
            <button style={styles.btnGhost} onClick={() => setModal(false)}>Cancel</button>
            <button style={styles.btnPrimary} onClick={createPlan} disabled={saving}>
              {saving ? 'Creating…' : 'Create & Open'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

const styles = {
  h1:        { margin:0, fontSize:22, fontWeight:800, color:'#0A0A0A' },
  btnPrimary:{ background:'#C19A6B', color:'#F5F5F5', border:'none', borderRadius:6, padding:'8px 14px', fontWeight:700, fontSize:13, cursor:'pointer' },
  btnGhost:  { background:'transparent', border:'1px solid #30363D', borderRadius:6, padding:'6px 12px', color:'#9A8A7A', fontSize:12, cursor:'pointer' },
  btnAction: { background:'transparent', border:'1px solid #30363D', borderRadius:6, padding:'5px 10px', color:'#9A8A7A', fontSize:11, cursor:'pointer' },
  planRow:   { background:'#FFFFFF', border:'1px solid #21262D', borderRadius:10, padding:'14px 16px', cursor:'pointer', display:'flex', alignItems:'center', gap:12 },
  empty:     { background:'#FFFFFF', border:'1px solid #21262D', borderRadius:10, padding:32, textAlign:'center', color:'#9A8A7A', fontSize:14 },
  label:     { fontSize:11, fontWeight:600, color:'#9A8A7A', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:5 },
  input:     { width:'100%', boxSizing:'border-box', background:'#F5F5F5', border:'1px solid #30363D', borderRadius:6, padding:'9px 12px', color:'#0A0A0A', fontSize:13, outline:'none', fontFamily:'inherit' },
};
